import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const CACHE_KEYS = {
  WATCHES: 'sm_cache_watches',
  SAVED_ARTICLES: 'sm_cache_saved_articles',
  INBOX: 'sm_cache_inbox',
  DISMISSED: 'sm_cache_dismissed'
};

// Hjälpfunktioner för säker lokal lagring
const readCache = (key, fallback = []) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    console.warn(`Kunde inte läsa lokal cache för ${key}:`, e);
    return fallback;
  }
};

const writeCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn(`Kunde inte spara lokal cache för ${key}:`, e);
  }
};

const clearLocalCache = () => {
  try {
    Object.values(CACHE_KEYS).forEach(k => localStorage.removeItem(k));
  } catch (e) {
    console.warn('Fel vid rensning av lokal cache:', e);
  }
};

export const useSupabaseData = (session) => {
  const userId = session?.user?.id;

  // --- STATE (Initieras direkt från cache för 0 ms laddtid) ---
  const [savedWatches, setSavedWatches] = useState(() => readCache(CACHE_KEYS.WATCHES, []));
  const [savedArticles, setSavedArticles] = useState(() => readCache(CACHE_KEYS.SAVED_ARTICLES, []));
  const [inboxMatches, setInboxMatches] = useState(() => readCache(CACHE_KEYS.INBOX, []));
  const [dismissedInboxItems, setDismissedInboxItems] = useState(() => readCache(CACHE_KEYS.DISMISSED, []));

  // --- HÄMT-FUNKTIONER ---
  const fetchWatches = useCallback(async () => {
    if (!userId) {
      setSavedWatches([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('watches')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setSavedWatches(data);
        writeCache(CACHE_KEYS.WATCHES, data);
      }
    } catch (err) {
      console.warn('Kunde inte nå Supabase för bevakningar (använder lokal cache):', err.message);
    }
  }, [userId]);

  const fetchInbox = useCallback(async () => {
    if (!userId) {
      setInboxMatches([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('daily_matches')
        .select('*, watches(title)')
        .eq('user_id', userId)
        .or('is_deleted.eq.false,is_deleted.is.null') 
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      if (data) {
        setInboxMatches(data);
        writeCache(CACHE_KEYS.INBOX, data);
      }
    } catch (err) {
      console.warn('Kunde inte nå Supabase för inkorg (använder lokal cache):', err.message);
    }
  }, [userId]);

  const fetchSavedArticles = useCallback(async () => {
    if (!userId) {
      setSavedArticles([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('saved_articles')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setSavedArticles(data);
        writeCache(CACHE_KEYS.SAVED_ARTICLES, data);
      }
    } catch (err) {
      console.warn('Kunde inte nå Supabase för sparade artiklar (använder lokal cache):', err.message);
    }
  }, [userId]);

  // --- AUTOMATISK HÄMTNING NÄR MAN LOGGAR IN / SESSION ÄNDRAS ---
  useEffect(() => {
    if (userId) {
      fetchWatches();
      fetchInbox();
      fetchSavedArticles();
    } else {
      clearLocalCache();
      setSavedWatches([]);
      setSavedArticles([]);
      setInboxMatches([]);
      setDismissedInboxItems([]);
    }
  }, [userId, fetchWatches, fetchInbox, fetchSavedArticles]);

  // --- RADERA-FUNKTIONER (Optimistic UI + Cache-uppdatering) ---
  const deleteWatch = async (id) => {
    setSavedWatches(prev => {
      const updated = prev.filter(w => w.id !== id);
      writeCache(CACHE_KEYS.WATCHES, updated);
      return updated;
    });

    try {
      const { error } = await supabase.from('watches').delete().eq('id', id);
      if (error) {
        console.error('Fel vid radering av bevakning i Supabase:', error);
        fetchWatches();
      }
    } catch (err) {
      console.error('Nätverksfel vid radering av bevakning:', err);
    }
  };

  const deleteSavedArticle = async (id) => {
    setSavedArticles(prev => {
      const updated = prev.filter(a => a.id !== id);
      writeCache(CACHE_KEYS.SAVED_ARTICLES, updated);
      return updated;
    });

    try {
      const { error } = await supabase.from('saved_articles').delete().eq('id', id);
      if (error) {
        console.error('Fel vid radering av sparad artikel i Supabase:', error);
        fetchSavedArticles();
      }
    } catch (err) {
      console.error('Nätverksfel vid radering av sparad artikel:', err);
    }
  };

  // --- DISMISS (SOFT DELETE) ---
  const dismissItem = async (id) => {
    // Optimistic UI: Göm direkt på skärmen och uppdatera lokalt minne
    setDismissedInboxItems(prev => {
      const updated = [...prev, id];
      writeCache(CACHE_KEYS.DISMISSED, updated);
      return updated;
    });

    try {
      const { error } = await supabase
        .from('daily_matches')
        .update({ is_deleted: true })
        .eq('id', id);
        
      if (error) console.error('Fel vid döljande av artikel i Supabase:', error);
    } catch (err) {
      console.error('Nätverksfel vid döljande av artikel:', err);
    }
  };

  // --- TÖM HELA INKORGEN (Sätt is_read = true på alla olästa) ---
  const clearInbox = async () => {
    // Optimistic UI: Markera allt som läst lokalt direkt
    setInboxMatches(prev => {
      const updated = prev.map(item => ({ ...item, is_read: true }));
      writeCache(CACHE_KEYS.INBOX, updated);
      return updated;
    });

    try {
      const { error } = await supabase
        .from('daily_matches')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Fel vid tömning av inkorg i Supabase:', error);
        fetchInbox();
      } else {
        fetchInbox();
      }
    } catch (err) {
      console.error('Nätverksfel vid tömning av inkorg:', err);
    }
  };

  // --- EXPORTERADE FUNKTIONER OCH STATE ---
  return {
    savedWatches, setSavedWatches, fetchWatches, deleteWatch,
    savedArticles, setSavedArticles, fetchSavedArticles, deleteSavedArticle,
    inboxMatches, setInboxMatches, fetchInbox,
    dismissedInboxItems, setDismissedInboxItems, dismissItem,
    clearInbox
  };
};